import {
  Notification,
  NotificationListRequest,
  NotificationListResponse,
  NotificationResponse,
  NotificationActionResponse,
  NotificationCreateRequest,
  NotificationUpdateRequest,
} from '@/types/notification';
import { MOCK_NOTIFICATIONS, formatRelativeTime } from '@/constants/notifications';

// Local state to track notifications (simulating database)
let notificationsData = [...MOCK_NOTIFICATIONS];

export const notificationService = {
  // Get notifications with filters and pagination
  async getNotifications(request: NotificationListRequest = {}): Promise<NotificationListResponse> {
    try {
      let filteredNotifications = [...notificationsData];

      // Apply filters
      if (request.filters?.isRead !== undefined) {
        filteredNotifications = filteredNotifications.filter(n => n.isRead === request.filters?.isRead);
      }

      if (request.filters?.type) {
        filteredNotifications = filteredNotifications.filter(n => n.type === request.filters?.type);
      }

      // Apply pagination
      const page = request.pagination?.page || 1;
      const limit = request.pagination?.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;

      const paginatedNotifications = filteredNotifications.slice(startIndex, endIndex);
      const totalCount = filteredNotifications.length;
      const unreadCount = filteredNotifications.filter(n => !n.isRead).length;
      const hasMore = endIndex < totalCount;

      return {
        content: {
          notifications: paginatedNotifications,
          totalCount,
          unreadCount,
          hasMore
        },
        succeeded: true,
        messages: []
      };
    } catch (error: any) {
      console.error('Get notifications error:', error);
      return {
        content: {
          notifications: [],
          totalCount: 0,
          unreadCount: 0,
          hasMore: false
        },
        succeeded: false,
        messages: [error.message || 'Failed to get notifications'],
      };
    }
  },

  // Get single notification by ID
  async getNotification(id: string): Promise<NotificationResponse> {
    try {
      const notification = notificationsData.find(n => n.id === id);

      if (notification) {
        return {
          content: notification,
          succeeded: true,
          messages: []
        };
      }

      return {
        content: null,
        succeeded: false,
        messages: ['Notification not found']
      };
    } catch (error: any) {
      console.error('Get notification error:', error);
      return {
        content: null,
        succeeded: false,
        messages: [error.message || 'Failed to get notification'],
      };
    }
  },

  // Create new notification
  async createNotification(request: NotificationCreateRequest): Promise<NotificationResponse> {
    try {
      const newNotification: Notification = {
        id: Date.now().toString(),
        title: request.title,
        message: request.message,
        time: 'Just now',
        timestamp: new Date(),
        isRead: false,
        type: request.type,
        userId: 'user1',
        metadata: request.metadata
      };

      notificationsData.unshift(newNotification);

      return {
        content: newNotification,
        succeeded: true,
        messages: ['Notification created successfully']
      };
    } catch (error: any) {
      console.error('Create notification error:', error);
      return {
        content: null,
        succeeded: false,
        messages: [error.message || 'Failed to create notification'],
      };
    }
  },

  // Update notification
  async updateNotification(request: NotificationUpdateRequest): Promise<NotificationResponse> {
    try {
      const index = notificationsData.findIndex(n => n.id === request.id);

      if (index !== -1) {
        notificationsData[index] = { ...notificationsData[index], ...request };

        return {
          content: notificationsData[index],
          succeeded: true,
          messages: ['Notification updated successfully']
        };
      }

      return {
        content: null,
        succeeded: false,
        messages: ['Notification not found']
      };
    } catch (error: any) {
      console.error('Update notification error:', error);
      return {
        content: null,
        succeeded: false,
        messages: [error.message || 'Failed to update notification'],
      };
    }
  },

  // Mark notification as read
  async markAsRead(id: string): Promise<NotificationActionResponse> {
    try {
      const notification = notificationsData.find(n => n.id === id);

      if (notification) {
        notification.isRead = true;
        return {
          content: { success: true },
          succeeded: true,
          messages: ['Notification marked as read']
        };
      }

      return {
        content: { success: false },
        succeeded: false,
        messages: ['Notification not found']
      };
    } catch (error: any) {
      console.error('Mark as read error:', error);
      return {
        content: { success: false },
        succeeded: false,
        messages: [error.message || 'Failed to mark notification as read'],
      };
    }
  },

  // Mark all notifications as read
  async markAllAsRead(_userId?: string): Promise<NotificationActionResponse> {
    try {
      notificationsData.forEach(n => n.isRead = true);

      return {
        content: { success: true },
        succeeded: true,
        messages: ['All notifications marked as read']
      };
    } catch (error: any) {
      console.error('Mark all as read error:', error);
      return {
        content: { success: false },
        succeeded: false,
        messages: [error.message || 'Failed to mark all notifications as read'],
      };
    }
  },

  // Delete notification
  async deleteNotification(id: string): Promise<NotificationActionResponse> {
    try {
      const index = notificationsData.findIndex(n => n.id === id);

      if (index !== -1) {
        notificationsData.splice(index, 1);
        return {
          content: { success: true },
          succeeded: true,
          messages: ['Notification deleted successfully']
        };
      }

      return {
        content: { success: false },
        succeeded: false,
        messages: ['Notification not found']
      };
    } catch (error: any) {
      console.error('Delete notification error:', error);
      return {
        content: { success: false },
        succeeded: false,
        messages: [error.message || 'Failed to delete notification'],
      };
    }
  },

  // Delete multiple notifications
  async deleteNotifications(ids: string[]): Promise<NotificationActionResponse> {
    try {
      const initialLength = notificationsData.length;
      notificationsData = notificationsData.filter(n => !ids.includes(n.id));
      const deletedCount = initialLength - notificationsData.length;

      return {
        content: { success: true },
        succeeded: true,
        messages: [`${deletedCount} notifications deleted successfully`]
      };
    } catch (error: any) {
      console.error('Delete notifications error:', error);
      return {
        content: { success: false },
        succeeded: false,
        messages: [error.message || 'Failed to delete notifications'],
      };
    }
  },

  // Get unread count
  async getUnreadCount(_userId?: string): Promise<{ content: { count: number }; succeeded: boolean; messages: string[] }> {
    try {
      const unreadCount = notificationsData.filter(n => !n.isRead).length;

      return {
        content: { count: unreadCount },
        succeeded: true,
        messages: []
      };
    } catch (error: any) {
      console.error('Get unread count error:', error);
      return {
        content: { count: 0 },
        succeeded: false,
        messages: [error.message || 'Failed to get unread count'],
      };
    }
  },

  // Utility function to format relative time
  formatRelativeTime,
};